import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck2, ChevronDown, ChevronRight } from 'lucide-react';
import {
  TASK_DUE_BUCKETS,
  TASK_DUE_BUCKET_LABELS,
  taskDueBucket,
  type ChecklistItem,
  type TaskDueBucket,
  type TaskListItem,
} from '@peles/shared';
import { useTaskPages } from '../../hooks/useEventOps';
import { TaskRow } from './TaskRow';
import { TaskConversationsProvider } from './TaskConversations';
import { TaskBulkBar, type BulkChange } from './TaskBulkBar';
import type { TaskFilters } from './TaskFilterBar';
import type { TaskAssignee } from './taskUi';

const BUCKET_TONE: Record<TaskDueBucket, string> = {
  overdue: 'text-red-700',
  today: 'text-amber-800',
  week: 'text-gray-900',
  later: 'text-gray-900',
  none: 'text-gray-500',
};

export interface TaskDueViewProps {
  filters: TaskFilters;
  todayKey: string;
  /** One event's tasks; every event's when absent. */
  eventId?: string;
  assignees: TaskAssignee[];
  onOpen: (task: TaskListItem) => void;
  onToggle: (task: ChecklistItem) => void;
  onDelete: (task: ChecklistItem) => void;
  onBulk: (itemIds: string[], change: BulkChange) => Promise<unknown>;
  bulkBusy?: boolean;
}

function TaskListSkeleton() {
  return (
    <div className="space-y-2" aria-label="Loading tasks">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-3">
          <div className="w-6 h-6 rounded-full bg-gray-100 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 w-2/3 rounded bg-gray-100 animate-pulse" />
            <div className="h-3 w-1/3 rounded bg-gray-100 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Tasks by when they are due — Overdue, Today, Next 7 days, Later, No date —
 * for one event or across all of them. The server filters and pages; the
 * groups are drawn with the same boundaries it counts by.
 */
export function TaskDueView({
  filters,
  todayKey,
  eventId,
  assignees,
  onOpen,
  onToggle,
  onDelete,
  onBulk,
  bulkBusy,
}: TaskDueViewProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const query = {
    eventId,
    q: filters.q,
    assignee: filters.assignee,
    due: filters.due,
    status: filters.status,
    date: todayKey,
  };
  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isPlaceholderData,
  } = useTaskPages(query);
  // The previous filter's tasks, shown while the new ones load, cannot be
  // acted on: they may not match what is now being asked for.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (listRef.current) listRef.current.inert = !!isPlaceholderData;
  }, [isPlaceholderData]);

  // A selection belongs to what was on screen when it was made: changing a
  // filter starts afresh, so a task filtered out of sight is never acted on.
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    setSelected(new Set());
  }, [queryKey]);

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const first = data?.pages[0];
  const total = first?.total ?? 0;

  const groups = useMemo(() => {
    if (filters.status === 'done') {
      return items.length ? [{ id: 'done', label: 'Done', items, count: total }] : [];
    }
    return TASK_DUE_BUCKETS.map((bucket) => ({
      id: bucket,
      label: TASK_DUE_BUCKET_LABELS[bucket],
      items: items.filter((t) => taskDueBucket(t.dueDate, todayKey) === bucket),
      count: first?.counts[bucket] ?? 0,
    })).filter((g) => g.items.length > 0);
  }, [items, first, total, filters.status, todayKey]);

  // Only what is on screen can be acted on — a task that dropped out of view
  // (ticked off, deleted elsewhere) leaves the selection.
  const visibleSelected = items.filter((t) => selected.has(t.id)).map((t) => t.id);

  function toggleSelect(task: ChecklistItem, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(task.id);
      else next.delete(task.id);
      return next;
    });
  }

  function toggleGroup(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (isLoading) return <TaskListSkeleton />;

  if (isError) {
    return (
      <div className="text-center py-10 bg-white rounded-xl border border-gray-200">
        <p className="text-sm text-gray-600">The tasks could not be loaded.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Try again
        </button>
      </div>
    );
  }

  const filtered = !!filters.q.trim() || filters.assignee.length > 0 || filters.due.length > 0;

  if (groups.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
        <CalendarCheck2 className="mx-auto h-10 w-10 text-green-500" />
        <p className="mt-3 font-medium text-gray-900">
          {filtered
            ? 'No tasks match these filters'
            : filters.status === 'done'
              ? 'Nothing finished yet'
              : 'All caught up'}
        </p>
        <p className="mt-1 text-sm text-gray-500">
          {filtered
            ? 'Try clearing a filter.'
            : filters.status === 'done'
              ? 'Tasks you complete will show here.'
              : 'There are no open tasks here.'}
        </p>
      </div>
    );
  }

  const shown = items.length;

  return (
    <TaskConversationsProvider>
      <div
        ref={listRef}
        className={`space-y-4 transition-opacity ${isPlaceholderData ? 'opacity-50' : ''}`}
        aria-busy={isPlaceholderData || undefined}
        data-testid="task-due-view"
      >
        {groups.map((group) => {
          const open = !collapsed.has(group.id);
          return (
            <section
              key={group.id}
              className="bg-white rounded-xl border border-gray-200"
              aria-label={group.label}
            >
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={open}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-left"
              >
                {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <h2
                  className={`text-sm font-semibold ${BUCKET_TONE[group.id as TaskDueBucket] ?? 'text-gray-900'}`}
                >
                  {group.label}
                </h2>
                <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                  {group.count}
                </span>
              </button>
              {open && (
                <div className="px-1 pb-2 divide-y divide-gray-100">
                  {group.items.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      todayKey={todayKey}
                      onToggle={onToggle}
                      onOpen={() => onOpen(task)}
                      onDelete={onDelete}
                      selected={selected.has(task.id)}
                      onSelect={toggleSelect}
                      context={
                        <>
                          {!eventId && task.event && (
                            <Link
                              to={`/events/${task.event.id}/checklist`}
                              className="text-primary-700 hover:underline"
                            >
                              {task.event.name}
                            </Link>
                          )}
                          {!eventId && task.event && ' · '}
                          {task.checklist.title || 'Task list'} · {task.section.name}
                        </>
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}

        {hasNextPage && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {isFetchingNextPage ? 'Loading…' : `Show more (${total - shown} more)`}
            </button>
          </div>
        )}

        <TaskBulkBar
          count={visibleSelected.length}
          assignees={assignees}
          todayKey={todayKey}
          busy={bulkBusy}
          onClear={() => setSelected(new Set())}
          onApply={async (change) => {
            const applied = await onBulk(visibleSelected, change);
            if (applied !== false) setSelected(new Set());
          }}
        />
      </div>
    </TaskConversationsProvider>
  );
}
